import { useQuery } from "@tanstack/react-query";
import { LoaderFunction, Outlet, useLoaderData } from "react-router-dom";

import { get, pget } from "api";
import TopNav from "molecules/navbars/TopNav";
import { Tabs } from "molecules/navigation/tabs/Tabs";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { notFalsy } from "toolbox/Guards";
import { waitObj } from "toolbox/Promise";

import { hasAvailableTreatmentsToSelect } from "./utils";

const getPromotions = pget("/promotions");

const load = async () => {
  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const promotions = queryClient.fetchQuery({
    queryKey: getPromotions.key(),
    queryFn: () => getPromotions(),
    staleTime: 1000 * 60 * 2,
  });

  const memberships = queryClient.fetchQuery(
    ["/platform-user-memberships"],
    () => get("/platform-user-memberships")
  );
  const treatments = queryClient.fetchQuery(["/platform-user-packages"], () =>
    get("/platform-user-packages")
  );

  const areRetailProductsVisible = queryClient.fetchQuery(
    ["/retailproducts/are-retail-products-visible"],
    () => get("/retailproducts/are-retail-products-visible"),
    { staleTime: 1000 * 10 }
  );

  const productsOrders = queryClient.fetchQuery(
    ["/retailproducts/{platformId}/orders"],
    async () => {
      const platformData = await platform;
      return await get("/retailproducts/{platformId}/orders", {
        platformId: platformData.id,
      });
    }
  );

  return waitObj({
    platform,
    promotions,
    memberships,
    treatments,
    areRetailProductsVisible,
    productsOrders,
  });
};

export type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletLayoutLoader: LoaderFunction = async () => {
  return await load();
};

const WalletLayout = () => {
  const sellingProductsFeatureFlagEnabled = useFeatureFlag("selling-products");
  const flagGiftCards = useFeatureFlag("giftcards");
  const data = useLoaderData() as LoaderData;
  const { data: promotions } = useQuery({
    ...getPromotions.keyed(),
    initialData: data.promotions,
  });

  const hasPromotions = promotions?.some((p) => !!p.redeemedOn);
  const notifyPromotions = promotions?.some(
    (p) => !!p.redeemedOn && p.expiresOn != null
  );

  const hasMemberships = data.memberships.length > 0;
  const hasTreatments = data.treatments.length > 0;
  const hasProducts = data.productsOrders.length > 0;
  const marketplaceEnabled =
    !!data.platform.platformSettings?.marketplaceEnabled;
  const hasRepeatCashEnabled =
    !!data.platform.platformSettings?.repeatCashEnabled;
  const isRetailProductsEnabled =
    sellingProductsFeatureFlagEnabled &&
    data.platform.platformSettings?.sellingProductsEnabled &&
    !!data.areRetailProductsVisible;
  const notifyMemberships = hasAvailableTreatmentsToSelect(data.memberships);
  const giftCardsEnabled =
    !!data.platform.platformSettings?.giftCardEnabled && flagGiftCards;

  const tabs = [
    (marketplaceEnabled || hasTreatments) && {
      to: "/wallet/treatments",
      label: "Treatments",
    },
    hasPromotions && {
      notify: notifyPromotions,
      to: "/wallet/offers",
      label: "Offers",
    },
    (isRetailProductsEnabled || hasProducts) && {
      to: "/wallet/products",
      label: "Products",
    },
    (marketplaceEnabled || hasMemberships) && {
      notify: notifyMemberships,
      to: "/wallet/memberships",
      label: "Memberships",
    },
    hasRepeatCashEnabled && {
      to: "/wallet/repeatcash",
      label: "RepeatCash",
    },
    giftCardsEnabled && {
      to: "/wallet/giftcards",
      label: "Gift Cards",
    },
  ].filter(notFalsy);

  return (
    <div className="flex min-h-full flex-col">
      <TopNav footer={<Tabs items={tabs} />}>
        <h1 className="text-xl">Wallet</h1>
      </TopNav>
      <div className="flex grow flex-col overflow-hidden bg-one">
        <Outlet />
      </div>
    </div>
  );
};

export default WalletLayout;
